/**
 * WatchlistStarButton — universal star button used across the app.
 *
 * Empty outline = not watched. Click opens a quick-add modal pre-filled with
 * the symbol; user enters optional target price + notes; submit adds.
 *
 * Filled gold = watched. Click asks "Remove {symbol} from watchlist?".
 */
import React, { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useWatchlist } from "@/lib/watchlist";

const WatchlistStarButton = ({
  cseSymbol,
  companyName,
  size = "icon", // 'icon' | 'sm' | 'md'
  className = "",
  testIdSuffix,
  iconClassName = "h-4 w-4",
  stopPropagation = true,
}) => {
  const { isWatched, add, remove } = useWatchlist();
  const watched = isWatched(cseSymbol);

  const [addOpen, setAddOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const onClick = (e) => {
    if (stopPropagation) e.stopPropagation();
    e.preventDefault();
    if (watched) setRemoveOpen(true);
    else setAddOpen(true);
  };

  const submitAdd = async () => {
    setBusy(true);
    try {
      await add(cseSymbol, {
        target_buy_price: target ? Number(target) : undefined,
        notes: notes || undefined,
      });
      toast.success(`Added ${cseSymbol} to watchlist`);
      setAddOpen(false);
      setTarget("");
      setNotes("");
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Failed to add to watchlist";
      if (status === 403) {
        toast.error(msg, { duration: 6000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitRemove = async () => {
    setBusy(true);
    try {
      await remove(cseSymbol);
      toast.success(`Removed ${cseSymbol} from watchlist`);
      setRemoveOpen(false);
    } catch {
      toast.error("Failed to remove from watchlist");
    } finally {
      setBusy(false);
    }
  };

  const buttonProps = {
    "data-testid": `watchlist-star-${testIdSuffix || cseSymbol}`,
    "aria-label": watched ? `Remove ${cseSymbol} from watchlist` : `Add ${cseSymbol} to watchlist`,
    onClick,
  };

  return (
    <>
      {size === "icon" ? (
        <button
          {...buttonProps}
          type="button"
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-muted/70 transition-colors text-muted-foreground ${className}`}
        >
          <Star
            className={`${iconClassName} ${
              watched
                ? "fill-[hsl(45_95%_55%)] text-[hsl(45_95%_55%)]"
                : "text-muted-foreground"
            }`}
          />
        </button>
      ) : (
        <Button
          variant={watched ? "secondary" : "outline"}
          size={size === "sm" ? "sm" : "default"}
          {...buttonProps}
          className={`gap-2 ${className}`}
        >
          <Star
            className={`${iconClassName} ${
              watched
                ? "fill-[hsl(45_95%_55%)] text-[hsl(45_95%_55%)]"
                : ""
            }`}
          />
          {watched ? "Watched" : "Watchlist"}
        </Button>
      )}

      {/* Quick-add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent
          className="sm:max-w-[420px]"
          data-testid="watchlist-quick-add-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>Add to watchlist</DialogTitle>
            <DialogDescription>
              {companyName ? `${companyName} — ${cseSymbol}` : cseSymbol}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="wl-target">Target buy price (optional)</Label>
              <Input
                id="wl-target"
                type="number"
                step="0.01"
                placeholder="e.g. 70.00"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                data-testid="watchlist-target-input"
              />
              <p className="text-[11px] text-muted-foreground">
                If set, we'll automatically create a price-below alert at this level.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wl-notes">Notes (optional)</Label>
              <Textarea
                id="wl-notes"
                rows={3}
                placeholder="e.g. Waiting for Q3 results"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="watchlist-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={submitAdd} disabled={busy} data-testid="watchlist-quick-add-submit">
              {busy ? "Adding…" : "Add to Watchlist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent
          data-testid="watchlist-remove-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {cseSymbol} from watchlist?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The auto-created price-below alert (if any) will be deactivated. You can
              add it back any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitRemove}
              disabled={busy}
              data-testid="watchlist-remove-confirm"
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white"
            >
              {busy ? "Removing…" : "Yes, remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WatchlistStarButton;
